import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { accessControlService } from '../services/accessControlService';
import { handleAuthenticationError } from '../utils/authErrorHandler';
import type { ScreenPermissionDto, UserAccessDto } from '../types';

export type PermissionErrorType = 'none' | 'network' | 'forbidden' | 'auth' | 'unknown';

interface PermissionContextType {
  /** Permissoes do usuario logado */
  permissions: ScreenPermissionDto[];
  /** Dados do usuario logado */
  user: UserAccessDto | null;
  /** Se o usuario e admin */
  isAdmin: boolean;
  /** Se esta carregando permissoes */
  isLoading: boolean;
  /** Mensagem de erro */
  error: string | null;
  /** Tipo do erro */
  errorType: PermissionErrorType;
  /** Se precisa de bootstrap (primeiro admin) */
  needsBootstrap: boolean;
  /** Verifica se usuario tem permissao para uma tela */
  hasPermission: (screenKey: string) => boolean;
  /** Recarrega permissoes do servidor */
  refreshPermissions: () => Promise<void>;
  /** Executa bootstrap do primeiro admin */
  doBootstrap: () => Promise<boolean>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { accounts } = useMsal();
  const isAuthenticated = accounts.length > 0;

  const [permissions, setPermissions] = useState<ScreenPermissionDto[]>([]);
  const [user, setUser] = useState<UserAccessDto | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<PermissionErrorType>('none');
  const [needsBootstrap, setNeedsBootstrap] = useState(false);

  const hasPermission = useCallback((screenKey: string): boolean => {
    // Admin tem acesso a tudo
    if (isAdmin) return true;

    // Verifica permissao especifica
    const permission = permissions.find(p => p.screenKey === screenKey);
    return permission?.isAllowed ?? false;
  }, [permissions, isAdmin]);

  const refreshPermissions = useCallback(async () => {
    if (!isAuthenticated) {
      setPermissions([]);
      setUser(null);
      setIsAdmin(false);
      setIsLoading(false);
      setError(null);
      setErrorType('none');
      setNeedsBootstrap(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setErrorType('none');

    try {
      const response = await accessControlService.getMyPermissions();

      if (response.needsBootstrap) {
        setNeedsBootstrap(true);
        setPermissions([]);
        setUser(null);
        setIsAdmin(false);
      } else if (response.user) {
        setUser(response.user);
        setPermissions(response.permissions);
        setIsAdmin(response.user.isAdmin);
        setNeedsBootstrap(false);
      }
    } catch (err: any) {
      console.error('Error loading permissions:', err);

      setPermissions([]);
      setUser(null);
      setIsAdmin(false);

      if (err.response?.status === 403) {
        // Usuario nao cadastrado ou desativado
        setError(err.response?.data?.message || 'Acesso negado');
        setErrorType('forbidden');
      } else if (err.response?.status === 401) {
        // Token invalido ou expirado - redireciona para login
        // Nota: O interceptor do apiClient ja deve ter tratado isso
        // Este e um fallback caso nao tenha
        setError('Sessao expirada. Redirecionando para login...');
        setErrorType('auth');
        await handleAuthenticationError();
      } else if (!err.response) {
        // Erro de rede - API nao disponivel
        setError('Nao foi possivel conectar ao servidor. Verifique se a API esta rodando.');
        setErrorType('network');
      } else {
        // Outro erro
        setError('Erro ao carregar permissoes');
        setErrorType('unknown');
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const doBootstrap = useCallback(async (): Promise<boolean> => {
    try {
      const newUser = await accessControlService.bootstrap();
      setUser(newUser);
      setIsAdmin(true);
      setNeedsBootstrap(false);
      setError(null);
      setErrorType('none');
      // Recarrega permissoes apos bootstrap
      await refreshPermissions();
      return true;
    } catch (err: any) {
      console.error('Error during bootstrap:', err);
      setError(err.response?.data?.message || 'Erro ao criar primeiro administrador');
      setErrorType('unknown');
      return false;
    }
  }, [refreshPermissions]);

  // Carrega permissoes quando usuario autentica
  useEffect(() => {
    refreshPermissions();
  }, [refreshPermissions]);

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        user,
        isAdmin,
        isLoading,
        error,
        errorType,
        needsBootstrap,
        hasPermission,
        refreshPermissions,
        doBootstrap
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};
